<template>
  <div class="selection:bg-brand-500 selection:text-white pb-20 overflow-x-hidden">
    <!-- Hero Section -->
    <header class="relative pt-20 pb-24 sm:pt-32 sm:pb-32 bg-card/30 border-b border-app">
      <div class="absolute inset-0 -z-10 bg-[radial-gradient(45%_45%_at_50%_50%,rgba(var(--core-brand-500),0.05)_0%,transparent_100%)]"></div>
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-base font-semibold leading-7 text-brand-500 uppercase tracking-widest mb-6">Our Mission</h1>
        <p class="mx-auto max-w-4xl text-5xl font-extrabold tracking-tight text-header sm:text-7xl leading-[1.1]">
          Empowering the world through <span class="bg-gradient-to-r to-brand-500 via-primary-500 from-secondary-500 bg-clip-text text-transparent font-extrabold">Unified Innovation</span>.
        </p>
        <p class="mx-auto mt-8 max-w-2xl text-xl text-body/80 leading-relaxed">
          At Dephics, we believe that software should be intuitive, integrated, and impactful. We're building an ecosystem where complexity dissolves into seamless experiences.
        </p>
      </div>
    </header>

    <!-- Vision & Story -->
    <section class="py-24">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                <div class="relative">
                    <div class="aspect-square rounded-[40px] overflow-hidden border border-app bg-card/40 backdrop-blur-xl p-4 shadow-2xl relative z-10">
                        <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1000" class="rounded-[30px] w-full h-full object-cover opacity-90" />
                        <div class="absolute -bottom-10 -right-10 h-64 w-64 bg-brand-500/10 blur-[80px] -z-10"></div>
                    </div>
                </div>
                <div class="space-y-10">
                    <div>
                        <h2 class="text-3xl font-bold text-header mb-6">Our Story</h2>
                        <div class="space-y-4 text-body/80 leading-relaxed">
                            <p>Founded in 2024, Dephics began with a simple observation: enterprise software was fragmented, frustrating, and slow. We set out to change that by creating a unified core that scales with your ambition.</p>
                            <p>Today, our ecosystem serves thousands of users across multiple modules, from promotional marketing to secure certification, all under one elegant roof.</p>
                        </div>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-6 border-t border-app">
                        <div>
                            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/10 text-brand-500 mb-4">
                                <Target class="h-6 w-6" />
                            </div>
                            <h4 class="font-bold text-header mb-2">Our Vision</h4>
                            <p class="text-sm text-body/80">To be the global standard for integrated digital experiences.</p>
                        </div>
                        <div>
                            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-4">
                                <Compass class="h-6 w-6" />
                            </div>
                            <h4 class="font-bold text-header mb-2">Our Goal</h4>
                            <p class="text-sm text-body/80">Reducing barriers to digital transformation for everyone.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Core Values -->
    <section class="py-24 bg-body border-y border-app">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="text-center mb-16">
                <h2 class="text-3xl font-bold text-header sm:text-4xl">Values That Drive Us</h2>
                <p class="mt-4 text-body/80">The principles we live by every single day.</p>
            </div>
            <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                <div v-for="value in values" :key="value.name" class="bg-card p-8 rounded-3xl border border-app transition-all hover:border-brand-500/30 hover:-translate-y-1 group">
                    <div class="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-header transition-colors group-hover:bg-brand-500 group-hover:text-white">
                        <component :is="value.icon" class="h-7 w-7" />
                    </div>
                    <h3 class="text-xl font-bold text-header mb-4">{{ value.name }}</h3>
                    <p class="text-sm text-body/80 leading-relaxed">{{ value.description }}</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Stats Section (Simplified) -->
    <section class="py-24">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-12">
                <div v-for="stat in stats" :key="stat.label">
                    <div class="text-4xl font-extrabold text-brand-500 mb-2">{{ stat.value }}</div>
                    <div class="text-sm font-bold text-body/80 uppercase tracking-widest">{{ stat.label }}</div>
                </div>
            </div>
        </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20">
        <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center bg-card border border-app p-16 rounded-[50px] shadow-2xl relative overflow-hidden">
            <div class="absolute -top-24 -right-24 h-64 w-64 bg-brand-500/5 blur-[100px]"></div>
            <h2 class="text-4xl font-extrabold text-header mb-6">Work with us Today</h2>
            <p class="text-body/80 text-lg mb-10 max-w-2xl mx-auto">Join the next generation of business management. Secure, integrated, and incredibly fast.</p>
            <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button @click="router.push('/member-registration')" class="rounded-2xl bg-brand-500 px-10 py-4 text-lg font-bold text-white hover:bg-brand-600 transition-all active:scale-95 shadow-xl shadow-brand-500/20">Join the Community</button>
                <button class="rounded-2xl border border-app px-10 py-4 text-lg font-bold text-header hover:bg-muted transition-all active:scale-95">Contact Sales</button>
            </div>
        </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { 
    Target, 
    Compass, 
    ShieldCheck, 
    Zap, 
    Users, 
    Heart 
} from 'lucide-vue-next'

definePageMeta({ name: 'About' })

const router = useRouter()

const values = [
    {
        name: 'Innovation',
        description: 'Constantly pushing the boundaries of what is possible with software.',
        icon: Zap
    },
    {
        name: 'Security',
        description: 'Bank-level protection for your most sensitive business data.',
        icon: ShieldCheck
    },
    {
        name: 'Community',
        description: 'Building software that connects people and creates opportunities.',
        icon: Users
    },
    {
        name: 'Integrity',
        description: 'Honest, transparent, and ethical practices in everything we do.',
        icon: Heart
    }
]

const stats = [
    { label: 'Active Modules', value: '14+' },
    { label: 'Uptime', value: '99.9%' },
    { label: 'Lines of Code', value: '2.5M+' },
    { label: 'Support 24/7', value: 'Yes' },
]
</script>

<style scoped>
.bg-card { background-color: rgb(var(--core-card-bg)); }
.bg-body { background-color: rgb(var(--core-body-bg)); }
.border-app { border-color: rgb(var(--core-app-border)); }
.text-header { color: rgb(var(--core-header-text)); }
.text-muted { color: rgb(var(--core-muted-text)); }
</style>
